package nl.appstuff.padelchecker.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.background
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import nl.appstuff.padelchecker.site.PageSeo
import nl.appstuff.padelchecker.site.SiteColors
import nl.appstuff.padelchecker.site.components.NavBar
import nl.appstuff.padelchecker.site.components.sections.FooterSection
import nl.appstuff.padelchecker.site.localization.LocalSiteLanguage
import nl.appstuff.padelchecker.site.localization.LocalSiteStrings
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

@Page
@Composable
fun VoorwaardenPage() {
    val seoStrings = LocalSiteStrings.current
    val language = LocalSiteLanguage.current
    PageSeo(
        title = seoStrings.termsSeoTitle,
        description = seoStrings.termsSeoDescription,
        canonicalPath = "/voorwaarden",
        lang = language.tag,
    )

    Column(modifier = Modifier.fillMaxWidth().minHeight(100.vh)) {
        NavBar()
        VoorwaardenContent()
        FooterSection()
    }
}

@Composable
private fun VoorwaardenContent() {
    val strings = LocalSiteStrings.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("flex", "1") }
            .background(SiteColors.backgroundLight)
            .padding(topBottom = 64.px, leftRight = 24.px),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(760.px),
            verticalArrangement = Arrangement.spacedBy(24.px),
        ) {
            SpanText(
                strings.termsTitle,
                modifier = Modifier
                    .fontSize(40.px)
                    .fontWeight(FontWeight.Bold)
                    .color(SiteColors.textLight),
            )
            SpanText(
                strings.termsLastUpdated,
                modifier = Modifier
                    .fontSize(14.px)
                    .color(SiteColors.textMutedLight),
            )

            VoorwaardenBlock(
                title = "1. DEFINITIES",
                text = """
                    - Padelchecker: de applicatie en bijbehorende diensten, aangeboden door Appstuff.
                    - Gebruiker: iedere natuurlijke persoon of rechtspersoon die gebruikmaakt van de app.
                    - Abonnement: een betaalde dienst met terugkerende kosten.
                    - Digitale dienst: de functionaliteit van de app zoals aangeboden via internet.
                """.trimIndent(),
            )

            VoorwaardenBlock(
                title = "2. TOEPASSELIJKHEID",
                text = """
                    Deze algemene voorwaarden zijn van toepassing op elk gebruik van Padelchecker en op alle overeenkomsten tussen jou en Padelchecker.

                    Door gebruik te maken van de app ga je akkoord met deze voorwaarden.
                """.trimIndent(),
            )

            VoorwaardenBlock(
                title = "3. DIENSTVERLENING",
                text = """
                    Padelchecker biedt gebruikers inzicht in padelbanen, beschikbaarheid, statistieken en/of gerelateerde functionaliteiten.

                    Wij doen ons best om de app correct en beschikbaar te houden, maar geven geen garanties over:
                    - foutloos functioneren
                    - continue beschikbaarheid
                """.trimIndent(),
            )

            VoorwaardenBlock(
                title = "4. ACCOUNT EN GEBRUIK",
                text = """
                    Je bent verantwoordelijk voor:
                    - het correct invullen van gegevens
                    - het veilig bewaren van je account

                    Het is niet toegestaan om:
                    - de app te misbruiken
                    - data te scrapen of te kopiëren
                    - illegale activiteiten uit te voeren via de app

                    Wij mogen accounts blokkeren bij misbruik.
                """.trimIndent(),
            )

            VoorwaardenBlock(
                title = "5. PRIJZEN EN ABONNEMENTEN",
                text = """
                    - Prijzen worden weergegeven in de app of op de website.
                    - Abonnementen worden automatisch verlengd, tenzij je opzegt.
                    - Betalingen kunnen verlopen via Apple App Store, Google Play Store of het web (via RevenueCat / Stripe / Paddle).
                """.trimIndent(),
            )

            VoorwaardenBlock(
                title = "6. BETALINGEN",
                text = """
                    Betalingen worden verwerkt via externe betaalproviders (zoals Apple, Google, Stripe of Paddle).

                    Padelchecker heeft geen directe controle over deze betalingen.
                """.trimIndent(),
            )

            VoorwaardenBlock(
                title = "7. ANNULERING EN OPZEGGING",
                text = """
                    - Je kunt je abonnement op elk moment opzeggen.
                    - Opzegging gaat in aan het einde van de lopende factureringsperiode.
                    - Je behoudt toegang tot de dienst tot het einde van die periode.
                """.trimIndent(),
            )

            VoorwaardenBlock(
                title = "8. HERROEPINGSRECHT (EU)",
                text = """
                    Als consument heb je normaal gesproken recht op een bedenktijd van 14 dagen bij online aankopen.

                    Voor digitale diensten geldt echter:
                    - Door aankoop en direct gebruik van de app stem je ermee in dat de dienst direct wordt geleverd.
                    - Daarmee doe je afstand van het herroepingsrecht, voor zover wettelijk toegestaan.
                """.trimIndent(),
            )

            VoorwaardenBlock(
                title = "9. REFUNDBELEID",
                text = """
                    Aankopen van digitale diensten zijn in principe niet restitueerbaar, tenzij wettelijk verplicht.
                """.trimIndent(),
            )

            VoorwaardenSubBlock(
                title = "9.1 Wettelijke uitzonderingen",
                text = """
                    Je hebt recht op een refund wanneer:
                    - de dienst niet werkt zoals beloofd
                    - de dienst niet geleverd wordt
                    - er sprake is van een technisch defect
                """.trimIndent(),
            )

            VoorwaardenSubBlock(
                title = "9.2 Platform-specifieke refunds",
                text = """
                    Afhankelijk van waar je hebt gekocht:
                    - Apple App Store: refunds via Apple support.
                    - Google Play Store: refunds via Google.
                    - Web (RevenueCat / Stripe / Paddle): refunds kunnen door ons worden verwerkt via RevenueCat. Bij een refund vervalt direct toegang tot de dienst.
                """.trimIndent(),
            )

            VoorwaardenSubBlock(
                title = "9.3 Vrijwillige refunds",
                text = """
                    Wij kunnen naar eigen inzicht een (gedeeltelijke) refund geven, maar zijn daartoe niet verplicht.
                """.trimIndent(),
            )

            VoorwaardenBlock(
                title = "10. AANSPRAKELIJKHEID",
                text = """
                    Padelchecker is niet aansprakelijk voor:
                    - indirecte schade
                    - verlies van data
                    - gemiste reserveringen of inkomsten

                    De aansprakelijkheid is beperkt tot het bedrag dat je hebt betaald voor de dienst.
                """.trimIndent(),
            )

            VoorwaardenBlock(
                title = "11. INTELLECTUEEL EIGENDOM",
                text = """
                    Alle rechten op de app, inclusief code, design en content, blijven eigendom van Padelchecker.
                """.trimIndent(),
            )

            VoorwaardenBlock(
                title = "12. WIJZIGINGEN",
                text = """
                    Wij mogen deze voorwaarden aanpassen.

                    Bij belangrijke wijzigingen informeren we je via de app of e-mail.
                """.trimIndent(),
            )

            VoorwaardenBlock(
                title = "13. TOEPASSELIJK RECHT",
                text = """
                    Op deze voorwaarden is Nederlands recht van toepassing.

                    Geschillen worden voorgelegd aan de bevoegde rechter in Nederland.
                """.trimIndent(),
            )

            VoorwaardenBlock(
                title = "14. LICENTIE EN APP STORES",
                text = """
                    Padelchecker wordt gelicentieerd, niet verkocht, aan jou. 
                    Je krijgt een beperkte, niet-exclusieve, niet-overdraagbare licentie om de app te gebruiken op apparaten die je bezit of beheert, in overeenstemming met de voorwaarden van het platform waarop je de app hebt gedownload.
                
                    Apple is geen partij bij deze overeenkomst en is niet verantwoordelijk voor de app, de inhoud of de dienstverlening.
                
                    Abonnementen die via de Apple App Store worden afgesloten, worden beheerd door Apple. Je kunt deze beheren en opzeggen via je Apple ID-instellingen.
                    Op abonnementen die via Apple worden afgesloten, is tevens de standaard Apple End User License Agreement (EULA) van toepassing:
                    https://www.apple.com/legal/internet-services/itunes/dev/stdeula/
                    
                    Abonnementen die via de Google Play Store worden afgesloten, worden beheerd door Google. Je kunt deze beheren en opzeggen via je Google-accountinstellingen.
                
                    Abonnementen die via het web worden afgesloten, worden beheerd via onze betaalproviders (zoals RevenueCat & Paddle).
                """.trimIndent(),
            )

            VoorwaardenBlock(
                title = "15. CONTACT",
                text = """
                    Voor vragen of support:

                    contact@padelchecker.nl
                """.trimIndent(),
            )
        }
    }
}

@Composable
private fun VoorwaardenBlock(title: String, text: String) {
    Column(verticalArrangement = Arrangement.spacedBy(8.px)) {
        SpanText(
            title,
            modifier = Modifier
                .fontSize(20.px)
                .fontWeight(FontWeight.SemiBold)
                .color(SiteColors.textLight),
        )
        VoorwaardenRichText(text)
    }
}

@Composable
private fun VoorwaardenSubBlock(title: String, text: String) {
    Column(verticalArrangement = Arrangement.spacedBy(8.px)) {
        SpanText(
            title,
            modifier = Modifier
                .fontSize(16.px)
                .fontWeight(FontWeight.SemiBold)
                .color(SiteColors.textLight),
        )
        VoorwaardenRichText(text)
    }
}

@Composable
private fun VoorwaardenRichText(text: String) {
    Column(verticalArrangement = Arrangement.spacedBy(12.px)) {
        parseVoorwaardenText(text).forEach { block ->
            when (block) {
                is VoorwaardenTextBlock.Paragraph -> {
                    SpanText(
                        block.text,
                        modifier = Modifier
                            .fontSize(15.px)
                            .lineHeight(1.7)
                            .color(SiteColors.textMutedLight),
                    )
                }

                is VoorwaardenTextBlock.BulletList -> {
                    Ul(
                        attrs = {
                            style {
                                color(SiteColors.textMutedLight)
                                margin(0.px)
                                paddingLeft(24.px)
                                property("display", "grid")
                                property("gap", "8px")
                            }
                        }
                    ) {
                        block.items.forEach { item ->
                            Li(
                                attrs = {
                                    style {
                                        fontSize(15.px)
                                        property("line-height", "1.7")
                                    }
                                }
                            ) {
                                Text(item)
                            }
                        }
                    }
                }
            }
        }
    }
}

private sealed interface VoorwaardenTextBlock {
    data class Paragraph(val text: String) : VoorwaardenTextBlock
    data class BulletList(val items: List<String>) : VoorwaardenTextBlock
}

private fun parseVoorwaardenText(text: String): List<VoorwaardenTextBlock> {
    val blocks = mutableListOf<VoorwaardenTextBlock>()
    val paragraphLines = mutableListOf<String>()
    val bulletItems = mutableListOf<String>()

    fun flushParagraph() {
        if (paragraphLines.isNotEmpty()) {
            blocks += VoorwaardenTextBlock.Paragraph(paragraphLines.joinToString(" "))
            paragraphLines.clear()
        }
    }

    fun flushBullets() {
        if (bulletItems.isNotEmpty()) {
            blocks += VoorwaardenTextBlock.BulletList(bulletItems.toList())
            bulletItems.clear()
        }
    }

    text.lines().forEach { rawLine ->
        val trimmed = rawLine.trim()
        when {
            trimmed.isEmpty() -> {
                flushBullets()
                flushParagraph()
            }

            trimmed.startsWith("- ") -> {
                flushParagraph()
                bulletItems += trimmed.removePrefix("- ").trim()
            }

            bulletItems.isNotEmpty() && rawLine.takeWhile { it == ' ' || it == '\t' }.isNotEmpty() -> {
                val lastIndex = bulletItems.lastIndex
                bulletItems[lastIndex] = bulletItems[lastIndex] + " " + trimmed
            }

            else -> {
                flushBullets()
                paragraphLines += trimmed
            }
        }
    }

    flushBullets()
    flushParagraph()
    return blocks
}
